// ====================================================================
// SAVE — localStorage persistence for the player's wallet and wardrobe
// ====================================================================
// First slice of the design plan's persistence story (§1): until server-side
// Chocabloc ID saves exist, coins, owned outfits, and equipped gear live in
// localStorage so the outfit store is meaningful across reloads and the
// game.html round-trip.
//
// Managed school Chromebooks can block storage entirely, so every touch of
// localStorage sits in a try/catch and the game runs fine with persistence
// silently off. Writes are event-driven (coin change, purchase, equip) —
// never per-frame.

const KEY = 'chockytown:save:v1';

const DEFAULTS = {
  coins: 0,
  totalCoins: 0,
  owned: ['cream'],            // outfit ids — the classic look is always owned
  equipped: { body: 'cream', head: null },
  quests: {},                  // quest id → active/done states (see game/*quest.js)
};

const VALID_QUEST_STATES = new Set(['digging', 'searching', 'chasing', 'done']);

let cache = null;

export function loadSave() {
  if (cache) return cache;
  cache = { ...DEFAULTS, equipped: { ...DEFAULTS.equipped }, quests: {} };
  try {
    const raw = localStorage.getItem(KEY);
    if (raw) {
      const data = JSON.parse(raw);
      if (Number.isFinite(data.coins)) cache.coins = Math.max(0, data.coins | 0);
      if (Number.isFinite(data.totalCoins)) cache.totalCoins = Math.max(0, data.totalCoins | 0);
      if (Array.isArray(data.owned)) cache.owned = [...new Set(['cream', ...data.owned.filter(id => typeof id === 'string')])];
      if (data.equipped && typeof data.equipped === 'object') {
        cache.equipped.body = typeof data.equipped.body === 'string' ? data.equipped.body : 'cream';
        cache.equipped.head = typeof data.equipped.head === 'string' ? data.equipped.head : null;
      }
      if (data.quests && typeof data.quests === 'object') {
        for (const [id, v] of Object.entries(data.quests)) {
          if (VALID_QUEST_STATES.has(v)) cache.quests[id] = v;
        }
      }
    }
  } catch { /* storage blocked or corrupt — run with defaults */ }
  return cache;
}

export function patchSave(patch) {
  const save = loadSave();
  Object.assign(save, patch);
  try {
    localStorage.setItem(KEY, JSON.stringify(save));
  } catch { /* storage blocked — keep the in-memory copy for this session */ }
  return save;
}
